import React, { useEffect, useMemo } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Space,
  Switch,
  Typography,
  message,
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import documentControlService from '@/services/documentControl.service';
import { apiErrorMessage } from '@/utils/apiErrorMessage';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';

const { Title, Text } = Typography;
const { TextArea } = Input;

const DocumentManagementFormPage: React.FC<{ mode?: 'create' | 'edit' }> = ({ mode }) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = mode === 'create' || !id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form] = Form.useForm();

  const originWatch = Form.useWatch('origin', form) || searchParams.get('origin') || 'INTERNAL';
  const familyWatch = Form.useWatch('doc_family', form);
  const departmentIdWatch = Form.useWatch('department_id', form);
  const trWatch = Form.useWatch('is_translation', form);

  const { data: catalog } = useQuery({
    queryKey: ['document-control', 'catalog'],
    queryFn: () => documentControlService.catalog(),
  });
  const { data: departments = [] } = useDepartmentsForSelection();

  const { data, isLoading } = useQuery({
    queryKey: ['document-control', id],
    queryFn: () => documentControlService.get(id!),
    enabled: !isNew && !!id,
  });

  const selectedFamily = useMemo(
    () => (catalog?.internal_families || []).find((f) => f.family === familyWatch),
    [catalog, familyWatch]
  );
  const departmentRequired =
    String(originWatch).toUpperCase() === 'INTERNAL' && !!selectedFamily?.requiresDept;

  useEffect(() => {
    if (!isNew || !catalog) return;
    const origin = (searchParams.get('origin') || 'INTERNAL').toUpperCase();
    form.setFieldsValue({
      origin,
      doc_family: origin === 'EXTERNAL' ? 'EXT_STD' : 'QSP',
      revision: '00',
      language: 'en',
      is_translation: false,
      revision_date: dayjs(),
    });
  }, [isNew, catalog, form, searchParams]);

  useEffect(() => {
    if (!data || isNew) return;
    form.setFieldsValue({
      origin: data.origin,
      document_level: data.document_level || undefined,
      doc_family: data.doc_family,
      document_number: data.document_number,
      title: data.title,
      description: data.description || undefined,
      department_id: data.department_id || undefined,
      revision: data.revision,
      revision_date: data.revision_date ? dayjs(data.revision_date) : undefined,
      effective_date: data.effective_date ? dayjs(data.effective_date) : undefined,
      language: data.language || 'en',
      is_translation: !!Number(data.is_translation),
      translation_of_id: data.translation_of_id || undefined,
      custodian_name: data.custodian_name || undefined,
      custodian_location: data.custodian_location || undefined,
      prepared_by_name: data.prepared_by_name || undefined,
      prepared_by_designation: data.prepared_by_designation || undefined,
      next_review_date: data.next_review_date ? dayjs(data.next_review_date) : undefined,
      publisher_or_source: data.publisher_or_source || undefined,
      moc_id: data.moc_id || undefined,
      notes: data.notes || undefined,
      nature_of_amendment: undefined,
    });
  }, [data, form, isNew]);

  const familyOptions = useMemo(() => {
    if (!catalog) return [];
    if (String(originWatch).toUpperCase() === 'EXTERNAL') {
      return catalog.external_families.map((f) => ({ value: f.family, label: f.label }));
    }
    return catalog.internal_families.map((f) => ({
      value: f.family,
      label: `Level ${f.level} — ${f.label}`,
    }));
  }, [catalog, originWatch]);

  const selectedDeptLabel = useMemo(() => {
    const d = departments.find((x) => x.id === departmentIdWatch);
    return d ? `${d.department_code} — ${d.department_name}` : null;
  }, [departments, departmentIdWatch]);

  const suggest = useMutation({
    mutationFn: () =>
      documentControlService.suggestNumber({
        origin: originWatch,
        doc_family: familyWatch,
        department_id: departmentIdWatch || undefined,
        is_translation: !!trWatch,
      }),
    onSuccess: (r) => form.setFieldsValue({ document_number: r.document_number }),
    onError: (e) => message.error(apiErrorMessage(e)),
  });

  const save = useMutation({
    mutationFn: async (values: any) => {
      const payload = {
        origin: values.origin,
        doc_family: values.doc_family,
        document_number: values.document_number,
        title: values.title,
        description: values.description || null,
        department_id: values.department_id || null,
        revision: values.revision || '00',
        revision_date: values.revision_date ? values.revision_date.format('YYYY-MM-DD') : null,
        effective_date: values.effective_date ? values.effective_date.format('YYYY-MM-DD') : null,
        language: values.language || 'en',
        is_translation: !!values.is_translation,
        translation_of_id: values.translation_of_id || null,
        custodian_name: values.custodian_name || null,
        custodian_location: values.custodian_location || null,
        prepared_by_name: values.prepared_by_name || null,
        prepared_by_designation: values.prepared_by_designation || null,
        next_review_date: values.next_review_date ? values.next_review_date.format('YYYY-MM-DD') : null,
        publisher_or_source: values.publisher_or_source || null,
        moc_id: values.moc_id || null,
        notes: values.notes || null,
        nature_of_amendment: values.nature_of_amendment || null,
      };
      if (isNew) return documentControlService.create(payload);
      return documentControlService.update(id!, payload);
    },
    onSuccess: (row) => {
      message.success(isNew ? 'Document created' : 'Document updated');
      qc.invalidateQueries({ queryKey: ['document-control'] });
      navigate(`/qms/document-management/${row.id}`);
    },
    onError: (e) => message.error(apiErrorMessage(e)),
  });

  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>
      <Space style={{ marginBottom: 16 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() =>
            navigate(isNew ? '/qms/document-management' : `/qms/document-management/${id}`)
          }
        >
          Back
        </Button>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            {isNew ? 'New controlled document' : 'Edit controlled document'}
          </Title>
          <Text type="secondary">Identification, numbering, custodian and revision metadata</Text>
        </div>
      </Space>

      <Card loading={!isNew && isLoading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={(v) => save.mutate(v)}
          initialValues={{ origin: 'INTERNAL', revision: '00', language: 'en' }}
        >
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="origin" label="Origin" rules={[{ required: true }]}>
                <Select
                  options={[
                    { value: 'INTERNAL', label: 'Internal' },
                    { value: 'EXTERNAL', label: 'External' },
                  ]}
                  disabled={!isNew}
                  onChange={() => form.setFieldsValue({ doc_family: undefined, document_number: undefined })}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="doc_family" label="Document family" rules={[{ required: true }]}>
                <Select
                  options={familyOptions}
                  showSearch
                  optionFilterProp="label"
                  onChange={() => form.setFieldsValue({ document_number: undefined })}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="department_id"
                label="Department"
                rules={
                  departmentRequired
                    ? [{ required: true, message: 'Select a department from Finance master' }]
                    : undefined
                }
                extra={
                  selectedDeptLabel
                    ? `Uses Finance master code for numbering: ${selectedDeptLabel}`
                    : 'From Finance → Departments master'
                }
              >
                <DepartmentSelect
                  allowClear={!departmentRequired}
                  placeholder="Select department"
                  onChange={() => form.setFieldsValue({ document_number: undefined })}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={16}>
              <Form.Item
                name="document_number"
                label="Document number"
                rules={[{ required: true, message: 'Document number is required' }]}
                extra="Suggested from company code + family + Finance department code (e.g. NTES-QSP-QAQC-01)."
              >
                <Input
                  addonAfter={
                    <Button type="link" size="small" onClick={() => suggest.mutate()} loading={suggest.isPending}>
                      Suggest
                    </Button>
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={5}>
              <Form.Item name="revision" label="Revision" rules={[{ required: true }]}>
                <Input placeholder="00" />
              </Form.Item>
            </Col>
            <Col xs={24} md={5}>
              <Form.Item name="revision_date" label="Revision date">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="title" label="Document title" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="description" label="Description / details">
                <TextArea rows={2} />
              </Form.Item>
            </Col>
            {String(originWatch).toUpperCase() === 'EXTERNAL' ? (
              <>
                <Col xs={24} md={12}>
                  <Form.Item name="publisher_or_source" label="Publisher / source">
                    <Input placeholder="API, ASTM, OEM, customer, …" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="next_review_date" label="Next external review">
                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                  </Form.Item>
                </Col>
              </>
            ) : null}
            <Col xs={24} md={8}>
              <Form.Item name="custodian_name" label="Custodian / holder">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="custodian_location" label="Location / point of use">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="prepared_by_name" label="Prepared by">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="prepared_by_designation" label="Prepared by designation">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="effective_date" label="Effective date">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="language" label="Language">
                <Select
                  options={[
                    { value: 'en', label: 'English' },
                    { value: 'ar', label: 'Arabic' },
                    { value: 'other', label: 'Other' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="is_translation" label="Translated copy (TR)" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="moc_id" label="MOC link (optional)">
                <Input placeholder="MOC header UUID" />
              </Form.Item>
            </Col>
            {isNew ? (
              <Col xs={24}>
                <Form.Item name="nature_of_amendment" label="Initial amendment note">
                  <Input placeholder="Initial Issue" />
                </Form.Item>
              </Col>
            ) : null}
            <Col xs={24}>
              <Form.Item name="notes" label="Notes">
                <TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>
          <Space>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={save.isPending}>
              Save
            </Button>
            <Button onClick={() => navigate(-1)}>Cancel</Button>
          </Space>
        </Form>
      </Card>
    </div>
  );
};

export default DocumentManagementFormPage;
